import { type FC } from "react";

import classNames from "classnames";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import type { AddressStored } from "@jti/fe-common";
import { Dialog, FormProvider, useForm, useTranslations } from "@jti/ui";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint/useDeviceBreakpoint";

import { AddressFormFields } from "@checkout-v2/formFields/components/AddressFormFields";
import type { AddressHomeDeliveryFieldConfig } from "@checkout-v2/formFields/factories/addressHomeDeliveryFieldFactory";

import styles from "../../AtHomeAddressSelectionDialog.module.scss";

type ShippingAddressDialogProps = {
  fieldConfig: AddressHomeDeliveryFieldConfig;
  immutableProvinceCode?: string;
  shippingAddressErrorMessage?: string;
  onAddressSaveClick: (address: AddressStored) => Promise<boolean | void>;
  onAddressFormCancelClick: () => void;
  onClose: () => void;
};

export const AddressFormDialog: FC<ShippingAddressDialogProps> = ({
  fieldConfig,
  immutableProvinceCode,
  shippingAddressErrorMessage,
  onAddressSaveClick,
  onAddressFormCancelClick,
  onClose,
}) => {
  const { __ } = useTranslations();
  const { breakpoint } = useDeviceBreakpoint();

  const localForm = useForm<Record<string, AddressStored | { immutableProvinceCode?: string } | undefined>>({
    defaultValues: {
      [fieldConfig.name]: fieldConfig.emptyAddress,
      ...(immutableProvinceCode !== undefined && { _hiddenValues: { immutableProvinceCode } }),
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = localForm;

  const handleSave = handleSubmit(async (data) => {
    await onAddressSaveClick(data[fieldConfig.name] as AddressStored);
    onClose();
  });

  return (
    <FormProvider {...localForm}>
      <Dialog
        closable={false}
        isOpened
        fullscreen={breakpoint === "mobile"}
        className={styles.dialog}
        dialogClassName={styles.dialogContainer}
        onClose={onClose}
      >
        <div className={classNames(styles.content, styles.contentTwoActions)}>
          <div className={styles.visuallyHidden} role="status" aria-live="polite">
            {__("cart.checkout.shipping_address.new_dialog.form_loaded")}
          </div>
          <div className={styles.header}>
            <h2 className={styles.title}>{__("cart.checkout.shipping_address.new_dialog.title")}</h2>
          </div>
          {shippingAddressErrorMessage && <AlertBanner tone="error" description={shippingAddressErrorMessage} />}
          <AddressFormFields fieldNameBase={fieldConfig.name}>
            <div className={styles.footer}>
              <Button
                className={styles.cta}
                variant="secondary"
                onClick={onAddressFormCancelClick}
                disabled={isSubmitting}
              >
                {__("cart.checkout.shipping_address.new_dialog.cancel")}
              </Button>

              <Button
                className={styles.cta}
                type="button"
                onClick={handleSave}
                pending={isSubmitting}
                data-testid="save-address-button"
                disabled={isSubmitting || localForm.formState.isValid === false}
              >
                {__("cart.checkout.shipping_address.new_dialog.save")}
              </Button>
            </div>
          </AddressFormFields>
        </div>
      </Dialog>
    </FormProvider>
  );
};
